/**
 * Copyright © 2022-2023 Delicious AI, LLC
 *
 * @author Stockton Jenkins <stockton.jenkins@deliciousai.com>
 */

import * as React from 'react'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'
import { Box, ButtonGroup, useTheme } from '@mui/material'
import { MuiFormModalProps } from './modal.types'
import ModalTitle from './ModalTitle'

const BootstrapDialog = styled(Dialog)(({ theme, sx }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
    ...sx,
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
  '& .MuiPaper-root': {
    [theme.breakpoints.only('xs')]: {
      minWidth: `calc(100% - ${theme.spacing(4)})`,
    },
    [theme.breakpoints.up('sm')]: {
      minWidth: '800px',
    },
  },
}))

export const MuiFormModal: React.FC<
  React.PropsWithChildren<MuiFormModalProps>
> = props => {
  const {
    title,
    subtitle,
    isOpen,
    handleClose,
    actionButtonsProps,
    otherButtonsProps,
    children: modalContent,
    sx,
  } = props
  const theme = useTheme()

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
      sx={sx}
    >
      <ModalTitle id="customized-dialog-title" onClose={handleClose}>
        <Typography variant="h5">{title}</Typography>
        <Typography
          variant="h6"
          sx={theme => ({
            [theme.breakpoints.only('xs')]: {
              fontSize: 16,
            },
          })}
        >
          {subtitle}
        </Typography>
      </ModalTitle>
      <DialogContent
        dividers={!!modalContent}
        sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}
      >
        <Box>{modalContent}</Box>
        {otherButtonsProps && otherButtonsProps.length && (
          <ButtonGroup>
            {otherButtonsProps
              .filter(buttonProps => !buttonProps.hide)
              .map(buttonProps => (
                <Button
                  key={`modal-button-${buttonProps.children}`}
                  {...buttonProps}
                  variant={buttonProps.variant || 'contained'}
                  disableElevation
                />
              ))}
          </ButtonGroup>
        )}
      </DialogContent>
      {!!actionButtonsProps.length && (
        <DialogActions>
          <ButtonGroup>
            {actionButtonsProps
              .filter(buttonProps => !buttonProps.hide)
              .map(buttonProps => (
                <Button
                  key={`modal-button-${buttonProps.children}`}
                  {...buttonProps}
                  variant={buttonProps.variant || 'contained'}
                  disableElevation
                />
              ))}
          </ButtonGroup>
        </DialogActions>
      )}
    </BootstrapDialog>
  )
}
